import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { TenancyTimelineView } from "@/components/TenancyTimelineView";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2, AlertTriangle, Clock } from "lucide-react";

// Feature 14 — Tenancy Timeline (FREE, read-only). No Plus gate.
export default function TenancyTimeline() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error | notfound

  const load = () => {
    setState("loading");
    api.get(`/properties/${propertyId}/tenancy-timeline`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch((e) => setState(e?.response?.status === 404 ? "notfound" : "error"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  // Resolve condition-photo thumbnails to authed URLs; sensitive events carry no thumbnail_ref.
  const events = (data?.events || []).map((e) => ({
    ...e,
    thumbnailUrl: e.thumbnail_ref ? fileUrl(e.thumbnail_ref) : null,
  }));

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24" data-testid="tenancy-timeline-page">
      <div className="max-w-3xl mx-auto px-4 pt-5">
        <button data-testid="tl-back-btn" onClick={() => navigate(`/property/${propertyId}`)}
          className="flex items-center gap-2 text-sm text-[#475569] hover:text-navy mb-4">
          <ChevronLeft size={18} /> Back to property
        </button>

        {state === "loading" && (
          <div className="grid place-items-center py-24" data-testid="tl-loading"><Loader2 className="animate-spin text-brand" size={26} /></div>
        )}

        {state === "notfound" && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#64748B]" data-testid="tl-notfound">
            We couldn't find this property.
          </div>
        )}

        {state === "error" && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center" data-testid="tl-error">
            <AlertTriangle size={22} className="text-amber-500 mx-auto" />
            <p className="mt-2 text-sm text-[#64748B]">We couldn't load your Tenancy Timeline. Please try again.</p>
            <Button variant="outline" data-testid="tl-error-retry" onClick={load} className="mt-3 h-10 rounded-xl">Retry</Button>
          </div>
        )}

        {state === "ok" && events.length === 0 && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 text-center" data-testid="tl-empty">
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand/10 text-brand mx-auto"><Clock size={24} /></span>
            <p className="mt-3 text-sm font-semibold text-navy">Nothing on your timeline yet</p>
            <p className="mt-1 text-xs text-[#64748B]">Capture photos, log a checkup or record a key date and it will appear here in order.</p>
          </div>
        )}

        {state === "ok" && events.length > 0 && (
          <TenancyTimelineView
            address={null}
            events={events}
            linkedCount={(data?.linked_property_ids || []).length}
            onOpenEvent={(e) => navigate(e.existing_route || `/property/${propertyId}`)}
          />
        )}
      </div>
    </div>
  );
}
